import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { INITIAL_UPLOAD_PROGRESS } from '@routes/sidebar/admin-center/creator/___pages___/elements/model/constants.ts';
import type { UploadProgress } from '@routes/sidebar/admin-center/creator/___pages___/elements/model/types.ts';
import type { UploadContentResponse } from '@routes/sidebar/admin-center/creator/___shared___/createResource.api.ts';
import { createApiResourceWithUploadProgress } from '@routes/sidebar/admin-center/creator/___shared___/createResource.api.ts';
import type {
    TaskAssessorOption,
    UserPathwayTaskDetailsResponse,
    UserPathwayTasksResponse,
    UserPathwayTaskWriteRequest,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/task.api.ts';
import { getApiLearnerTasks } from '@routes/sidebar/learning/___pages___/learning-overview/api/task.api.ts';
import {
    ASSESSOR_DECISION,
    getApiLearnerTask,
    getApiTaskAssessors,
    putApiLearnerTask,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/task.api.ts';
import { extractError } from '@utils/slice.ts';
import { isEmpty } from 'lodash';

interface LearningTaskSlice {
    taskData: UserPathwayTaskDetailsResponse | null;
    tasksData: UserPathwayTasksResponse;
    assessors: TaskAssessorOption[];
    error: Record<string, any> | null;
    isResubmitTaskModalOpened: boolean;
    isChangeAssessorModalOpened: boolean;
    isEvidenceFileReminderModalOpened: boolean;
    isRequestTaskExemptionModalOpened: boolean;
    isTaskInRedoFlow: boolean;
    uploadedResourcePreviewId: string | null;
    uploadProgress: UploadProgress;
    isFilePreviewLoading: boolean;
    isUploadResourcePending: boolean;
    isGetLearnerTaskPending: boolean;
    isUpdateLearnerTaskPending: boolean;
    isGetTaskAssessorsPending: boolean;
    isGetLearnerTasksPending: boolean;
}

const initialState: LearningTaskSlice = {
    taskData: null,
    tasksData: [],
    assessors: [],
    error: null,
    isResubmitTaskModalOpened: false,
    isRequestTaskExemptionModalOpened: false,
    isTaskInRedoFlow: true,
    uploadedResourcePreviewId: null,
    uploadProgress: INITIAL_UPLOAD_PROGRESS,
    isFilePreviewLoading: false,
    isChangeAssessorModalOpened: false,
    isEvidenceFileReminderModalOpened: false,
    isUploadResourcePending: false,
    isGetLearnerTaskPending: false,
    isUpdateLearnerTaskPending: false,
    isGetTaskAssessorsPending: false,
    isGetLearnerTasksPending: false,
};

export const getLearnerTask = createAsyncThunk<
    UserPathwayTaskDetailsResponse,
    { pathwayId: string; pathwayItemId: number },
    { rejectValue: any }
>('learningTask/getLearnerTask', ({ pathwayId, pathwayItemId }, { rejectWithValue }) =>
    getApiLearnerTask(pathwayId, pathwayItemId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getLearnerTasks = createAsyncThunk<UserPathwayTasksResponse, string, { rejectValue: any }>(
    'learningTask/getLearnerTasks',
    (pathwayId, { rejectWithValue }) =>
        getApiLearnerTasks(pathwayId)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const updateLearnerTask = createAsyncThunk<
    UserPathwayTaskDetailsResponse,
    { pathwayId: string; pathwayItemId: number; payload: UserPathwayTaskWriteRequest },
    { rejectValue: any }
>('learningTask/updateLearnerTask', ({ pathwayId, pathwayItemId, payload }, { rejectWithValue }) =>
    putApiLearnerTask(pathwayId, pathwayItemId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getTaskAssessors = createAsyncThunk<TaskAssessorOption[], void, { rejectValue: any }>(
    'learningTask/getTaskAssessors',
    (_, { rejectWithValue }) =>
        getApiTaskAssessors()
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const uploadTaskEvidenceResource = createAsyncThunk<
    UploadContentResponse[],
    {
        formData: FormData;
        onUploadProgress?: (progress: number) => void;
        signal?: AbortSignal;
    },
    { rejectValue: string[] }
>('learningTask/createElementResource', ({ formData, onUploadProgress, signal }, { rejectWithValue }) =>
    createApiResourceWithUploadProgress(formData, onUploadProgress, signal)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const learningTaskSlice = createSlice({
    name: 'learningTask',
    initialState,
    reducers: {
        setIsResubmitTaskModalOpened(state, action: PayloadAction<boolean>) {
            state.isResubmitTaskModalOpened = action.payload;
        },
        setIsEvidenceFileReminderModalOpened(state, action: PayloadAction<boolean>) {
            state.isEvidenceFileReminderModalOpened = action.payload;
        },
        setIsChangeAssessorModalOpened(state, action: PayloadAction<boolean>) {
            state.isChangeAssessorModalOpened = action.payload;
        },
        setIsRequestTaskExemptionModalOpened(state, action: PayloadAction<boolean>) {
            state.isRequestTaskExemptionModalOpened = action.payload;
        },
        resetLearnerTask() {
            return initialState;
        },
        setUploadedResourcePreviewId(state, action: PayloadAction<string | null>) {
            state.uploadedResourcePreviewId = action.payload;
        },
        setUploadProgress(state, action: PayloadAction<Partial<LearningTaskSlice['uploadProgress']>>) {
            state.uploadProgress = {
                ...state.uploadProgress,
                ...action.payload,
            };
        },
        clearUploadProgress(state) {
            state.uploadProgress = INITIAL_UPLOAD_PROGRESS;
        },
        clearUploadedResourcePreview(state) {
            state.uploadedResourcePreviewId = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getLearnerTask.pending, (state) => {
                state.isGetLearnerTaskPending = true;
                state.error = null;
            })
            .addCase(getLearnerTask.fulfilled, (state, action) => {
                state.isGetLearnerTaskPending = false;
                state.taskData = action.payload;
                state.isTaskInRedoFlow = action.payload.assessorFeedback?.decision === ASSESSOR_DECISION.RedoRequired;
            })
            .addCase(getLearnerTask.rejected, (state, action) => {
                state.isGetLearnerTaskPending = false;
                state.taskData = null;
                state.error = extractError(action);
            })
            .addCase(getLearnerTasks.pending, (state) => {
                state.isGetLearnerTasksPending = true;
                state.error = null;
            })
            .addCase(getLearnerTasks.fulfilled, (state, action) => {
                state.isGetLearnerTasksPending = false;
                state.tasksData = action.payload;
            })
            .addCase(getLearnerTasks.rejected, (state, action) => {
                state.isGetLearnerTasksPending = false;
                state.tasksData = [];
                state.error = extractError(action);
            })
            .addCase(updateLearnerTask.pending, (state) => {
                state.isUpdateLearnerTaskPending = true;
                state.error = null;
            })
            .addCase(updateLearnerTask.fulfilled, (state, action) => {
                state.isUpdateLearnerTaskPending = false;
                state.taskData = action.payload;
                state.isTaskInRedoFlow = action.payload.assessorFeedback?.decision === ASSESSOR_DECISION.RedoRequired;
            })
            .addCase(updateLearnerTask.rejected, (state, action) => {
                state.isUpdateLearnerTaskPending = false;
                state.error = extractError(action);
            })
            .addCase(getTaskAssessors.pending, (state) => {
                state.isGetTaskAssessorsPending = true;
                state.error = null;
            })
            .addCase(getTaskAssessors.fulfilled, (state, action) => {
                state.isGetTaskAssessorsPending = false;
                state.assessors = action.payload;
            })
            .addCase(getTaskAssessors.rejected, (state, action) => {
                state.isGetTaskAssessorsPending = false;
                state.assessors = [];
                state.error = extractError(action);
            });
        builder.addCase(uploadTaskEvidenceResource.pending, (state) => {
            state.isUploadResourcePending = true;
            state.error = null;
        });
        builder.addCase(uploadTaskEvidenceResource.fulfilled, (state, action) => {
            state.uploadedResourcePreviewId = null;
            if (!isEmpty(action.payload)) {
                state.uploadedResourcePreviewId = action.payload[0].contentId;
            }
            state.isUploadResourcePending = false;
        });
        builder.addCase(uploadTaskEvidenceResource.rejected, (state, action) => {
            state.isUploadResourcePending = false;
            state.uploadedResourcePreviewId = null;
            state.error = extractError(action);
        });
    },
});

export const {
    setIsResubmitTaskModalOpened,
    setIsChangeAssessorModalOpened,
    setIsEvidenceFileReminderModalOpened,
    setIsRequestTaskExemptionModalOpened,
    resetLearnerTask,
    setUploadProgress,
    clearUploadProgress,
    clearUploadedResourcePreview,
    setUploadedResourcePreviewId,
} = learningTaskSlice.actions;
export default learningTaskSlice.reducer;
